import { NgModule } from "@angular/core";
import { Routes, RouterModule } from "@angular/router";
import { AuthGuard } from "./core/guards/auth/auth.guard";
import { PageNotFoundComponent } from "./shared/components/page-not-found/page-not-found.component";
import { RoleGuard } from "./core/guards/role/role.guard";
import { Roles } from "./shared/constants";
import { ResourcelandingComponent } from "./shared/components/resourcelanding/resourcelanding.component";
import { BrowserSupportComponent } from "./shared/components/browser-support/browser-support.component";
import { PlaybooksListComponent } from "./company/playbooks/playbooks-list/playbooks-list.component";
import { HandleSubDomainRedirectComponent } from "./handle-sub-domain-redirect/handle-sub-domain-redirect.component";
import { PageTemplateEditorComponent } from "./shared/components/page-template-editor/page-template-editor.component";
import { CloseCurrentTabComponent } from "./shared/components/close-current-tab/close-current-tab.component";
import { ThankYouPageComponent } from "./shared/components/thank-you-page/thank-you-page.component";

const routes: Routes = [
  {
    path: "account",
    loadChildren: () =>
      import("./account/account.module").then((m) => m.AccountModule),
  },
  {
    path: "browser/support",
    component: BrowserSupportComponent,
    data: {
      topNavigation: true,
      sideNavigation: true,
      selectedModule: "",
      title: "Browser Support",
    },
  },
  {
    path: "companies",
    loadChildren: () =>
      import("./companies/companies.module").then((m) => m.CompaniesModule),
    canActivateChild: [AuthGuard, RoleGuard],
    runGuardsAndResolvers: "always",
    data: {
      topNavigation: true,
      sideNavigation: false,
      selectedModule: "company",
      title: "Companies",
      role: [
        Roles.SuperVPlaybookAdmin,
        Roles.CompanyReader,
        Roles.VPlaybookReader,
        Roles.StandardVPlaybookAdmin,
        Roles.SpecificVPlaybookAdmin,
      ],
    },
  },
  {
    path: "profile",
    loadChildren: () =>
      import("./profile/profile.module").then((m) => m.ProfileModule),
    canActivate: [AuthGuard],
    runGuardsAndResolvers: "always",
    data: {
      topNavigation: true,
      sideNavigation: false,
      selectedModule: "profile",
      title: "Profile",
    },
  },
  {
    path: "settings",
    loadChildren: () =>
      import("./settings/settings.module").then((m) => m.SettingsModule),
    canActivateChild: [AuthGuard],
    runGuardsAndResolvers: "always",
    data: {
      topNavigation: true,
      sideNavigation: true,
      selectedModule: "Settings",
      title: "Settings",
    },
  },
  {
    path: "company",
    loadChildren: () =>
      import("./company/company.module").then((m) => m.CompanyModule),
    canActivate: [AuthGuard],
    canActivateChild: [RoleGuard],
    runGuardsAndResolvers: "always",
    data: {
      role: [
        Roles.SuperVPlaybookAdmin,
        Roles.Administrator,
        Roles.SalesManager,
        Roles.User,
        Roles.PlaybookOwner,
        Roles.StandardVPlaybookAdmin,
        Roles.SpecificVPlaybookAdmin,
        Roles.VPlaybookReader,
        Roles.CompanyReader,
      ],
    },
  },
  {
    path: "people",
    loadChildren: () =>
      import("./admin/admin.module").then((m) => m.AdminModule),
    canActivate: [AuthGuard, RoleGuard],
    runGuardsAndResolvers: "always",
    data: {
      topNavigation: true,
      sideNavigation: true,
      selectedModule: "Admins",
      title: "People",
      role: [Roles.SuperVPlaybookAdmin],
    },
  },
  {
    path: "templates",
    loadChildren: () =>
      import("./templates/templates.module").then((m) => m.TemplatesModule),
    canActivate: [AuthGuard, RoleGuard],
    runGuardsAndResolvers: "always",
    data: {
      topNavigation: true,
      sideNavigation: false,
      selectedModule: "Templates",
      title: "Templates",
      role: [Roles.SuperVPlaybookAdmin],
    },
  },
  {
    path: "vbuilder",
    loadChildren: () =>
      import("./v-builder-admin/v-builder-admin.module").then(
        (m) => m.VBuilderAdminModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    runGuardsAndResolvers: "always",
    data: {
      topNavigation: true,
      sideNavigation: false,
      selectedModule: "VBuilderAdmin",
      title: "VBuilderAdmin",
      role: [Roles.SuperVPlaybookAdmin],
    },
  },
  {
    path: "playbooks",
    component: PlaybooksListComponent,
    canActivate: [AuthGuard, RoleGuard],
    data: {
      topNavigation: true,
      sideNavigation: true,
      selectedModule: "Playbooks",
      title: "Playbooks",
      role: [
        Roles.PlaybookOwner,
        Roles.SalesManager,
        Roles.User,
        Roles.Administrator,
      ],
    },
  },
  {
    path: "playbooks/category/:categoryId",
    component: PlaybooksListComponent,
    canActivate: [AuthGuard, RoleGuard],
    data: {
      topNavigation: true,
      sideNavigation: true,
      selectedModule: "Playbooks",
      title: "Playbooks",
      role: [
        Roles.PlaybookOwner,
        Roles.SalesManager,
        Roles.User,
        Roles.Administrator,
      ],
    },
  },
  {
    path: "playbook",
    loadChildren: () =>
      import(
        "../../projects/playbook-viewer/src/app/playbook-viewer/playbook-viewer.module"
      ).then((m) => m.PlaybookViewerModule),
    canActivate: [AuthGuard],
    runGuardsAndResolvers: "always",
  },
  {
    path: "playbookeditor",
    loadChildren: () =>
      import("./playbook-editor/playbook-editor.module").then(
        (m) => m.PlaybookEditorModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    runGuardsAndResolvers: "always",
    data: {
      role: [
        Roles.SuperVPlaybookAdmin,
        Roles.Administrator,
        Roles.PlaybookOwner,
        Roles.StandardVPlaybookAdmin,
        Roles.SpecificVPlaybookAdmin,
      ],
    },
  },
  {
    path: "reportdescription",
    loadChildren: () =>
      import(
        "./shared/modules/report-description/report-description.module"
      ).then((m) => m.ReportDescriptionModule),
    canActivate: [AuthGuard, RoleGuard],
    runGuardsAndResolvers: "always",
    data: {
      topNavigation: true,
      sideNavigation: true,
      selectedModule: "reportdescriptionTemplate",
      title: "Report Description",
      role: [Roles.SuperVPlaybookAdmin],
    },
  },
  {
    path: "unscubscibescheduled",
    loadChildren: () =>
      import(
        "./unsubscribe-schedule-report/unsubcribe-schedule-report.module"
      ).then((m) => m.UnsubcribeScheduleReportModule),
    data: {
      topNavigation: false,
      sideNavigation: false,
      title: "Unsubscribe Schedule Report",
    },
  },
  {
    path: "playbookstranslation",
    loadChildren: () =>
      import("./translation/playbook-translation.module").then(
        (m) => m.PlaybookTranslationModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      topNavigation: false,
      sideNavigation: false,
      title: "Playbook Translation",
      role: [
        Roles.SuperVPlaybookAdmin,
        Roles.Administrator,
        Roles.PlaybookOwner,
        Roles.StandardVPlaybookAdmin,
        Roles.SpecificVPlaybookAdmin,
      ],
    },
  },
  {
    path: "home/resourcelanding",
    component: ResourcelandingComponent,
    canActivate: [AuthGuard],
    data: {
      topNavigation: false,
      sideNavigation: false,
      title: "vPlaybook Resource",
    },
  },
  {
    path: "home/Resourcelanding",
    component: ResourcelandingComponent,
    canActivate: [AuthGuard],
    data: {
      topNavigation: false,
      sideNavigation: false,
      title: "vPlaybook Resource",
    },
  },
  {
    path: "Home/resourcelanding",
    component: ResourcelandingComponent,
    canActivate: [AuthGuard],
    data: {
      topNavigation: false,
      sideNavigation: false,
      title: "vPlaybook Resource",
    },
  },
  {
    path: "pagetemplate/:pageTemplateId/editor",
    component: PageTemplateEditorComponent,
    canActivate: [AuthGuard, RoleGuard],
    runGuardsAndResolvers: "always",
    data: {
      topNavigation: false,
      sideNavigation: false,
      title: "Page Template Editor",
      role: [
        Roles.SuperVPlaybookAdmin,
        Roles.Administrator,
        Roles.StandardVPlaybookAdmin,
      ],
    },
  },
  {
    path: "Home/Resourcelanding",
    component: ResourcelandingComponent,
    canActivate: [AuthGuard],
    data: {
      topNavigation: false,
      sideNavigation: false,
      title: "vPlaybook Resource",
    },
  },
  {
    path: "api/Assets/Resource/:assetId",
    component: ResourcelandingComponent,
    canActivate: [AuthGuard],
    data: {
      topNavigation: false,
      sideNavigation: false,
      title: "vPlaybook Resource",
    },
  },
  {
    path: "api/Assets/Download/:assetId",
    component: ResourcelandingComponent,
    canActivate: [AuthGuard],
    data: {
      topNavigation: false,
      sideNavigation: false,
      title: "vPlaybook Resource",
    },
  },
  {
    path: "assets/resource/:assetId",
    component: ResourcelandingComponent,
    canActivate: [AuthGuard],
    data: {
      topNavigation: false,
      sideNavigation: false,
      title: "vPlaybook Resource",
    },
  },
  {
    path: "assets/download/:assetId",
    component: ResourcelandingComponent,
    canActivate: [AuthGuard],
    data: {
      topNavigation: false,
      sideNavigation: false,
      title: "vPlaybook Resource",
    },
  },
  // TODO: patch remove unnecessary code
  // create url seriliazer to convert the url to lower case for resources and download path only.
  {
    path: "assets/Resource/:assetId",
    component: ResourcelandingComponent,
    canActivate: [AuthGuard],
    data: {
      topNavigation: false,
      sideNavigation: false,
      title: "vPlaybook Resource",
    },
  },
  {
    path: "assets/Download/:assetId",
    component: ResourcelandingComponent,
    canActivate: [AuthGuard],
    data: {
      topNavigation: false,
      sideNavigation: false,
      title: "vPlaybook Resource",
    },
  },
  {
    path: "Assets/resource/:assetId",
    component: ResourcelandingComponent,
    canActivate: [AuthGuard],
    data: {
      topNavigation: false,
      sideNavigation: false,
      title: "vPlaybook Resource",
    },
  },
  {
    path: "Assets/download/:assetId",
    component: ResourcelandingComponent,
    canActivate: [AuthGuard],
    data: {
      topNavigation: false,
      sideNavigation: false,
      title: "vPlaybook Resource",
    },
  },
  {
    path: "Assets/Resource/:assetId",
    component: ResourcelandingComponent,
    canActivate: [AuthGuard],
    data: {
      topNavigation: false,
      sideNavigation: false,
      title: "vPlaybook Resource",
    },
  },
  {
    path: "Assets/Download/:assetId",
    component: ResourcelandingComponent,
    canActivate: [AuthGuard],
    data: {
      topNavigation: false,
      sideNavigation: false,
      title: "vPlaybook Resource",
    },
  },
  {
    path: "subdomainredirect",
    component: HandleSubDomainRedirectComponent,
  },
  {
    path: "handlelogoffas",
    component: HandleSubDomainRedirectComponent,
  },
  {
    path: "error",
    component: PageNotFoundComponent,
  },
  {
    path: "close",
    component: CloseCurrentTabComponent,
  },
  {
    path: "thankyou",
    component: ThankYouPageComponent,
  },
  {
    path: "",
    redirectTo: "account",
    pathMatch: "full",
  },
  {
    path: "**",
    component: PageNotFoundComponent,
    pathMatch: "full",
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { onSameUrlNavigation: "reload" })],
  exports: [RouterModule],
  declarations: [],
})
export class AppRoutingModule {}
